// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/OrgRoleFunction.yaml
// Generated: 2026-06-15T05:18:46.876Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { orgRoleFunctionService } from '@/services/core/OrgRoleFunctionService';
import { CreateOrgRoleFunction, UpdateOrgRoleFunction, OrgRoleFunction } from '@/types/core/OrgRoleFunction';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for OrgRoleFunction
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const orgrolefunctionKeys = {
  all: (tenantId?: string | null) => ['orgrolefunctions', tenantId] as const,
  lists: (tenantId?: string | null) => [...orgrolefunctionKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...orgrolefunctionKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...orgrolefunctionKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...orgrolefunctionKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all OrgRoleFunctions
 */
export function useOrgRoleFunctions(options?: Omit<UseQueryOptions<OrgRoleFunction[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<OrgRoleFunction[], Error>({
    queryKey: orgrolefunctionKeys.lists(tenantId),
    queryFn: () => orgRoleFunctionService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single OrgRoleFunction by ID
 */
export function useOrgRoleFunction(
  id: string,
  options?: Omit<UseQueryOptions<OrgRoleFunction | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<OrgRoleFunction | undefined, Error>({
    queryKey: orgrolefunctionKeys.detail(id, tenantId),
    queryFn: () => orgRoleFunctionService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new OrgRoleFunction
 */
export function useCreateOrgRoleFunction() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<OrgRoleFunction, Error, CreateOrgRoleFunction, { previousOrgRoleFunctions?: OrgRoleFunction[] }>({
    mutationFn: (data: CreateOrgRoleFunction) => orgRoleFunctionService.create(data),
    onMutate: async (newOrgRoleFunction) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: orgrolefunctionKeys.lists(tenantId) });

      // Snapshot previous value
      const previousOrgRoleFunctions = queryClient.getQueryData<OrgRoleFunction[]>(orgrolefunctionKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<OrgRoleFunction[]>(orgrolefunctionKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newOrgRoleFunction, id: (newOrgRoleFunction as { id?: string }).id ?? 'temp-' + Date.now() } as OrgRoleFunction
      ]);

      return { previousOrgRoleFunctions };
    },
    onError: (_err, _newOrgRoleFunction, context) => {
      // Rollback on error
      if (context?.previousOrgRoleFunctions) {
        queryClient.setQueryData(orgrolefunctionKeys.lists(tenantId), context.previousOrgRoleFunctions);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: orgrolefunctionKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing OrgRoleFunction
 */
export function useUpdateOrgRoleFunction() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<OrgRoleFunction, Error, { id: string; data: UpdateOrgRoleFunction }, { previousOrgRoleFunction?: OrgRoleFunction; previousOrgRoleFunctions?: OrgRoleFunction[] }>({
    mutationFn: ({ id, data }) => orgRoleFunctionService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: orgrolefunctionKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: orgrolefunctionKeys.lists(tenantId) });

      // Snapshot previous values
      const previousOrgRoleFunction = queryClient.getQueryData<OrgRoleFunction>(orgrolefunctionKeys.detail(id, tenantId));
      const previousOrgRoleFunctions = queryClient.getQueryData<OrgRoleFunction[]>(orgrolefunctionKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<OrgRoleFunction>(orgrolefunctionKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as OrgRoleFunction);

      // Optimistically update list
      queryClient.setQueryData<OrgRoleFunction[]>(orgrolefunctionKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as OrgRoleFunction : item))
      );

      return { previousOrgRoleFunction, previousOrgRoleFunctions };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousOrgRoleFunction) {
        queryClient.setQueryData(orgrolefunctionKeys.detail(id, tenantId), context.previousOrgRoleFunction);
      }
      if (context?.previousOrgRoleFunctions) {
        queryClient.setQueryData(orgrolefunctionKeys.lists(tenantId), context.previousOrgRoleFunctions);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: orgrolefunctionKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: orgrolefunctionKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a OrgRoleFunction
 */
export function useDeleteOrgRoleFunction() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousOrgRoleFunctions?: OrgRoleFunction[] }>({
    mutationFn: (id: string) => orgRoleFunctionService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: orgrolefunctionKeys.lists(tenantId) });

      // Snapshot previous value
      const previousOrgRoleFunctions = queryClient.getQueryData<OrgRoleFunction[]>(orgrolefunctionKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<OrgRoleFunction[]>(orgrolefunctionKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousOrgRoleFunctions };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousOrgRoleFunctions) {
        queryClient.setQueryData(orgrolefunctionKeys.lists(tenantId), context.previousOrgRoleFunctions);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: orgrolefunctionKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSaveOrgRoleFunction() {
  const createMutation = useCreateOrgRoleFunction();
  const updateMutation = useUpdateOrgRoleFunction();

  return {
    mutate: (params: { id?: string; data: CreateOrgRoleFunction | UpdateOrgRoleFunction }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdateOrgRoleFunction }, options);
      } else {
        return createMutation.mutate(params.data as CreateOrgRoleFunction, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
